class Fido2Manager {

  private coerceToBase64Url(thing: unknown): string {
    let value = thing;
    if (Array.isArray(value)) {
      value = Uint8Array.from(value);
    }
    if (value instanceof ArrayBuffer) {
      value = new Uint8Array(value);
    }
    if (value instanceof Uint8Array) {
      let str = "";
      const len = value.byteLength;

      for (let i = 0; i < len; i++) {
        str += String.fromCharCode(value[i]);
      }
      value = window.btoa(str);
    }
    if (typeof value !== "string") {
      throw new Error("could not coerce to string");
    }
    return value.replace(/\+/g, "-").replace(/\//g, "_").replace(/=*$/g, "");
  }

  private coerceToArrayBuffer(thing: unknown): ArrayBuffer {
    let value = thing;
    if (typeof value === "string") {
      value = value.replace(/-/g, "+").replace(/_/g, "/");
      const str = window.atob(value);
      const bytes = new Uint8Array(str.length);
      for (let i = 0; i < str.length; i++) {
        bytes[i] = str.charCodeAt(i);
      }
      value = bytes;
    }

    if (Array.isArray(value)) {
      value = new Uint8Array(value);
    }

    if (value instanceof Uint8Array) {
      value = value.buffer;
    }

    if (!(value instanceof ArrayBuffer)) {
      throw new TypeError("could not coerce to ArrayBuffer");
    }

    return value;
  }

  public async handleFidoAttestation(fidoAttestationChallenge: string | null): Promise<string> {
    if (!fidoAttestationChallenge) {
      throw new Error(`Fido2 attestation challenge invalid`);
    }

    const makeCredentialOptions = JSON.parse(fidoAttestationChallenge);
    makeCredentialOptions.challenge = this.coerceToArrayBuffer(makeCredentialOptions.challenge);
    makeCredentialOptions.user.id = this.coerceToArrayBuffer(makeCredentialOptions.user.id);
    makeCredentialOptions.excludeCredentials = makeCredentialOptions.excludeCredentials.map((c: { id: unknown }) => {
      c.id = this.coerceToArrayBuffer(c.id);
      return c;
    });
    if (makeCredentialOptions.authenticatorSelection.authenticatorAttachment === null) {
      makeCredentialOptions.authenticatorSelection.authenticatorAttachment = undefined;
    }

    const promise = navigator.credentials.create({ publicKey: makeCredentialOptions });

    return promise.then((newCredential: Credential | null) => {
      const cred = newCredential as PublicKeyCredential;
      const response = cred.response as AuthenticatorAttestationResponse;
      const attestationObject = new Uint8Array(response.attestationObject);
      const clientDataJson = new Uint8Array(response.clientDataJSON);
      const rawId = new Uint8Array(cred.rawId);

      const data = {
        id: cred.id,
        rawId: this.coerceToBase64Url(rawId),
        type: cred.type,
        extensions: cred.getClientExtensionResults(),
        response: {
          AttestationObject: this.coerceToBase64Url(attestationObject),
          clientDataJson: this.coerceToBase64Url(clientDataJson)
        }
      };
      return JSON.stringify(data);
    },
      (reason) => {
        throw new Error(`Failed to create Fido2 credentials using built-in in browser function: ${reason}`);
      });
  }

  public async handleFidoLogin(fidoLoginChallenge: string | null): Promise<string> {
    if (!fidoLoginChallenge) {
      throw new Error(`Fido2 login challenge invalid`);
    }

    const makeAssertionOptions = JSON.parse(fidoLoginChallenge);
    const challenge = makeAssertionOptions.challenge.replace(/-/g, "+").replace(/_/g, "/");
    makeAssertionOptions.challenge = Uint8Array.from(atob(challenge), c => c.charCodeAt(0));

    makeAssertionOptions.allowCredentials.forEach((listItem: { id: string }) => {
      const fixedId = listItem.id.replace(/_/g, "/").replace(/-/g, "+");
      listItem.id = Uint8Array.from(atob(fixedId), c => c.charCodeAt(0)) as unknown as string;
    });

    // ask browser for credentials (browser will ask connected authenticators)
    const promise = navigator.credentials.get({ publicKey: makeAssertionOptions });
    return promise.then((assertedCredential: Credential | null) => {
      const cred = assertedCredential as PublicKeyCredential;
      const response = cred.response as AuthenticatorAssertionResponse;
      const authData = new Uint8Array(response.authenticatorData);
      const clientDataJson = new Uint8Array(response.clientDataJSON);
      const rawId = new Uint8Array(cred.rawId);
      const sig = new Uint8Array(response.signature);
      const data = {
        id: cred.id,
        rawId: this.coerceToBase64Url(rawId),
        type: cred.type,
        extensions: cred.getClientExtensionResults(),
        response: {
          authenticatorData: this.coerceToBase64Url(authData),
          clientDataJson: this.coerceToBase64Url(clientDataJson),
          signature: this.coerceToBase64Url(sig)
        }
      };
      return JSON.stringify(data);
    },
      (reason) => {
        throw new Error(`Failed to get Fido2 credentials using built-in browser function: ${reason}`);
      });
  }
}


export { Fido2Manager };
